import type React from 'react';
import { useState } from 'react';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import {
    faAnglesDown,
    faAnglesUp,
    faAngleDown,
    faAngleUp,
    faStar as faStarSolid,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetProjectCatalogDocument, ToggleProjectStarDocument } from '@/graphql/types';
import type { ProjectOverviewRow } from '../types';
import type { TableProps } from '../../../table/Table';
import { SwarmIconClasses } from '../../../../enums';
import classNames from '../../../../utils/classNames';
import { OptionsMenu } from '../components/OptionsMenu';
import { SwarmName } from '../components/SwarmName';
import { ProjectAlertsCell, ProjectDeviceStatusCell } from '../components/ProjectSummaryIndicators';
import type { ProjectRowSummary } from '../graphql/projectSummary';
import { createProjectMenuItems } from '../utils/menuItems/projectMenuItems';
import { hasAtLeastPermission, PERMISSION } from '../../sharing/utils';
import { runMutationWithRefresh } from '../utils/menuItems/menuItemActions';
import Tooltip from '../../../../utils/tooltip';
import { IconButton } from '../../../form/Button';
import { getProjectDetailsId } from '../hooks/useExpandedProjectRows';
import { ROW_ICON_BUTTON_CLASSES } from '../components/ProjectMeasuringPointRow/cellClasses';

interface StarredProjects {
    count: number;
    limit: number;
}

interface ColumnConfig {
    getIsRowExpanded: (project: ProjectOverviewRow) => boolean;
    onToggleExpand: (project: ProjectOverviewRow) => void;
    expandAll: boolean;
    onExpandAllChange: (expandAll: boolean) => void;
    canShowProjectOptions: (project: ProjectOverviewRow) => boolean;
    showProjectAttributes: boolean;
    isArchivedView: boolean;
    // Undefined until the catalog has loaded.
    starredProjects: StarredProjects | undefined;
    getProjectSummary: (project: ProjectOverviewRow) => ProjectRowSummary | null;
}

function ExpandAllToggle({
    expandAll,
    onExpandAllChange,
}: {
    expandAll: boolean;
    onExpandAllChange: (expandAll: boolean) => void;
}) {
    const label = expandAll ? gettext('COLLAPSE_ALL_PROJECTS') : gettext('EXPAND_ALL_PROJECTS');

    return (
        <Tooltip rounded content={label} placement="right">
            <IconButton
                id="expand-all-projects"
                buttonClassName={ROW_ICON_BUTTON_CLASSES}
                icon={expandAll ? faAnglesUp : faAnglesDown}
                ariaLabel={label}
                aria-expanded={expandAll}
                onClick={() => onExpandAllChange(!expandAll)}
            />
        </Tooltip>
    );
}

function ExpandButton({
    isExpanded,
    detailsId,
    onToggleExpand,
    data,
}: {
    isExpanded: boolean;
    detailsId: string;
    onToggleExpand: (project: ProjectOverviewRow) => void;
    data: ProjectOverviewRow;
}) {
    return (
        <IconButton
            id={`expand-project-${data.projectId}`}
            buttonClassName={ROW_ICON_BUTTON_CLASSES}
            icon={isExpanded ? faAngleUp : faAngleDown}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                onToggleExpand(data);
            }}
            aria-expanded={isExpanded}
            aria-controls={detailsId}
            ariaLabel={isExpanded ? gettext('COLLAPSE') : gettext('EXPAND')}
        />
    );
}

function getStarTooltip({
    isStarred,
    isStarringAvailable,
    isBlockedByLimit,
    starredProjects,
}: {
    isStarred: boolean;
    isStarringAvailable: boolean;
    isBlockedByLimit: boolean;
    starredProjects: StarredProjects;
}): string {
    if (!isStarringAvailable) {
        return gettext('PROJECT_STAR_UNAVAILABLE_IN_ARCHIVE');
    }

    const params = { count: starredProjects.count, limit: starredProjects.limit };
    if (isBlockedByLimit) {
        return interpolate(gettext('PROJECT_STAR_LIMIT_REACHED'), params, true);
    }

    return interpolate(
        isStarred ? gettext('PROJECT_UNSTAR_WITH_COUNT') : gettext('PROJECT_STAR_WITH_COUNT'),
        params,
        true
    );
}

function ProjectStarButton({
    project,
    isStarringAvailable,
    starredProjects,
}: {
    project: ProjectOverviewRow;
    isStarringAvailable: boolean;
    starredProjects: StarredProjects;
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { isStarred, projectId } = project;
    const ariaLabel = isStarred ? gettext('PROJECT_UNSTAR') : gettext('PROJECT_STAR');
    const isBlockedByLimit = !isStarred && starredProjects.count >= starredProjects.limit;
    const isDisabled = !isStarringAvailable || isBlockedByLimit;

    function handleStarClick(event: React.MouseEvent<HTMLButtonElement>) {
        event.stopPropagation();
        setIsSubmitting(true);

        void runMutationWithRefresh({
            mutation: ToggleProjectStarDocument,
            variables: {
                id: projectId,
            },
            refetchQueries: [GetProjectCatalogDocument],
        }).finally(() => {
            setIsSubmitting(false);
        });
    }

    // The tooltip shows how many projects are starred. A disabled button gets no hover or focus, so
    // the wrapper carries the tooltip.
    const tooltipContent = getStarTooltip({
        isStarred,
        isStarringAvailable,
        isBlockedByLimit,
        starredProjects,
    });

    return (
        <Tooltip
            rounded
            content={tooltipContent}
            trigger="mouseenter focusin"
            placement="bottom-start"
        >
            <div
                className={classNames('inline-flex', isDisabled && 'cursor-not-allowed')}
                {...(isDisabled ? { role: 'group', tabIndex: 0, 'aria-label': ariaLabel } : {})}
            >
                <button
                    type="button"
                    className={classNames(
                        ROW_ICON_BUTTON_CLASSES,
                        'disabled:opacity-60',
                        isDisabled ? 'text-gray-400' : 'text-primary'
                    )}
                    aria-label={ariaLabel}
                    aria-pressed={isStarred}
                    disabled={isSubmitting || isDisabled}
                    onClick={handleStarClick}
                >
                    <FontAwesomeIcon icon={isStarred ? faStarSolid : faStarRegular} />
                </button>
            </div>
        </Tooltip>
    );
}

export function createProjectCatalogColumns({
    getIsRowExpanded,
    onToggleExpand,
    expandAll,
    onExpandAllChange,
    getProjectSummary,
    canShowProjectOptions,
    showProjectAttributes,
    isArchivedView,
    starredProjects,
}: ColumnConfig): TableProps<ProjectOverviewRow>['columns'] {
    // The archived tab shows the stars, but they cannot be changed there.
    const isStarringAvailable = !isArchivedView;

    return [
        {
            key: 'starred',
            title: gettext('PROJECT_STARRED_COLUMN'),
            cellClassName: 'overflow-visible',
            // Starring a project needs view access.
            render: (_column, data) =>
                starredProjects && hasAtLeastPermission(data.permission, PERMISSION.View) ? (
                    <ProjectStarButton
                        project={data}
                        isStarringAvailable={isStarringAvailable}
                        starredProjects={starredProjects}
                    />
                ) : null,
        },
        {
            key: 'name',
            title: gettext('NAME'),
            cellClassName: 'overflow-hidden',
            render: (_column, data) => (
                <SwarmName name={data.projectName} iconClass={SwarmIconClasses[data.swarmType]} />
            ),
        },
        {
            key: 'city',
            title: gettext('CITY'),
            render: (_column, data) => data.city || gettext('UNKNOWN'),
        },
        {
            key: 'alerts',
            title: gettext('ALERTS'),
            cellClassName: 'overflow-visible',
            render: (_column, data) => <ProjectAlertsCell summary={getProjectSummary(data)} />,
        },
        {
            key: 'deviceStatus',
            title: gettext('STATUS'),
            colSpan: 2,
            cellClassName: 'overflow-visible',
            render: (_column, data) => (
                <ProjectDeviceStatusCell
                    summary={getProjectSummary(data)}
                    total={data.measuringPointCount}
                />
            ),
        },
        {
            key: 'total',
            title: gettext('TOTAL'),
            render: (_column, data) => data.measuringPointCount,
        },
        {
            key: 'options',
            title: gettext('OPTIONS'),
            className: 'text-right',
            cellClassName: 'overflow-visible text-right',
            render: (_column, data) => {
                const projectMenuItems = canShowProjectOptions(data)
                    ? createProjectMenuItems(data, { showProjectAttributes, isArchivedView })
                    : [];

                return (
                    projectMenuItems.length > 0 && (
                        <OptionsMenu items={projectMenuItems} ariaLabel={gettext('OPTIONS')} />
                    )
                );
            },
        },
        {
            key: 'expand',
            title: <ExpandAllToggle expandAll={expandAll} onExpandAllChange={onExpandAllChange} />,
            className: 'text-center',
            cellClassName: 'text-center',
            render: (_column, data) => (
                <ExpandButton
                    isExpanded={getIsRowExpanded(data)}
                    detailsId={getProjectDetailsId(data)}
                    onToggleExpand={onToggleExpand}
                    data={data}
                />
            ),
        },
    ];
}
